import { isFileEntryType } from './entryCardMenuPermissions';

export type BreadcrumbApiItem = { name?: string | null; url?: string | null };

export function isFileEntry(item: any): boolean {
  if (!item || typeof item !== 'object') return false;
  const entryType = item.entryType;
  if (
    entryType === 'Folder' ||
    entryType === 'Project' ||
    entryType === 'Version' ||
    String(entryType) === 'Folder' ||
    String(entryType) === 'Project' ||
    String(entryType) === 'Version'
  ) {
    return false;
  }
  return isFileEntryType(entryType);
}

export function normalizeSiblingNavPath(rawPath: string): string {
  return String(rawPath || '')
    .replace(/^\//, '')
    .split('#')[0]
    .split('?')[0]
    .replace(/\/versions\/?$/, '')
    .trim();
}

/** Parent entry path — stable while browsing files in the same folder. */
export function getSiblingParentKeyFromFilePath(filePath: string): string | null {
  const parts = normalizeSiblingNavPath(filePath).split('/').filter(Boolean);
  if (parts.length <= 1) return null;
  parts.pop();
  const parent = parts.join('/');
  if (isAmbiguousSiblingParentKey(parent)) return null;
  return parent;
}

/** Tek segment scope prefix (int/ext) API'de geçerli parent URL değil — breadcrumb beklenmeli. */
export function isAmbiguousSiblingParentKey(parentKey: string | null | undefined): boolean {
  if (!parentKey) return true;
  const parts = normalizeSiblingNavPath(parentKey).split('/').filter(Boolean);
  if (parts.length === 0) return true;
  if (parts.length === 1 && (parts[0] === 'int' || parts[0] === 'ext')) return true;
  return false;
}

export function resolveSiblingParentKey(
  breadcrumbItems: BreadcrumbApiItem[] | null | undefined,
  filePath: string
): string | null {
  const fromBreadcrumb = getParentEntryUrlFromBreadcrumbItems(breadcrumbItems);
  if (fromBreadcrumb && !isAmbiguousSiblingParentKey(fromBreadcrumb)) {
    return fromBreadcrumb;
  }
  return getSiblingParentKeyFromFilePath(filePath);
}

/** Breadcrumb items: last = current file, one before = parent with siblings. */
export function getParentEntryUrlFromBreadcrumbItems(
  items: BreadcrumbApiItem[] | null | undefined
): string | null {
  if (!Array.isArray(items) || items.length < 2) return null;
  const parent = items[items.length - 2];
  const url = String(parent?.url ?? '').trim();
  return url || null;
}

export function filterSiblingFiles(children: any[] | null | undefined): any[] {
  if (!Array.isArray(children)) return [];
  return children.filter(isFileEntry);
}

/** Files only; keeps parent `children` order (folders skipped). */
export function buildSiblingFilesFromParentChildren(children: any[] | null | undefined): any[] {
  return filterSiblingFiles(children);
}

/** Identity — list order must stay stable for the same parent. */
export function sortSiblingFilesForNavigation(files: any[]): any[] {
  return files ?? [];
}

/**
 * Current file index in the (stable) sibling list, derived from the URL path.
 * URL is the single source of truth: `int/<uuid>` matches a sibling `path`.
 */
export function getSiblingFileNavigationIndexByPath(
  files: any[],
  currentPath?: string | null
): number {
  if (!files.length) return -1;
  const normalized = normalizeSiblingNavPath(String(currentPath ?? ''));
  if (!normalized) return -1;
  return files.findIndex(
    (f) => normalizeSiblingNavPath(String(f.path ?? '')) === normalized
  );
}

export function getSiblingFileNavigationIndex(
  files: any[],
  currentFileId?: number | null,
  currentFilePath?: string | null
): number {
  if (!files.length) return -1;

  if (currentFileId != null) {
    const byId = files.findIndex(
      (f) => f.id === currentFileId || Number(f.id) === Number(currentFileId)
    );
    if (byId >= 0) return byId;
  }

  const normalizedPath = currentFilePath ? normalizeSiblingNavPath(currentFilePath) : '';
  if (normalizedPath) {
    const byPath = files.findIndex(
      (f) => normalizeSiblingNavPath(String(f.path ?? '')) === normalizedPath
    );
    if (byPath >= 0) return byPath;
  }

  return -1;
}
